/**
 * Best-effort browser coordinates for discovery / location sync.
 */

let lastGeolocationError = null;

export function getLastGeolocationError() {
  return lastGeolocationError;
}

function setGeolocationError(error) {
  lastGeolocationError = error?.message ? error : null;
}

function requestPosition({ enableHighAccuracy, timeout, maximumAge }) {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position.coords.latitude);
        const longitude = Number(position.coords.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          resolve({
            error: "INVALID",
            message: "GPS returned invalid coordinates.",
          });
          return;
        }
        const accuracy = Number(position.coords.accuracy);
        resolve({
          latitude,
          longitude,
          ...(Number.isFinite(accuracy) ? { accuracyMeters: accuracy } : {}),
        });
      },
      (err) => {
        const code = Number(err?.code);
        if (code === 1) {
          resolve({
            error: "PERMISSION_DENIED",
            message:
              "Location permission denied. Tap the icon next to your browser's address bar → Permissions → Location → Allow, then try again.",
          });
          return;
        }
        if (code === 2) {
          resolve({
            error: "POSITION_UNAVAILABLE",
            message:
              "GPS is off or unavailable. Turn on Location in your phone's Settings, then try again.",
          });
          return;
        }
        if (code === 3) {
          resolve({
            error: "TIMEOUT",
            message: "Location request timed out. Move to an open area and try again.",
          });
          return;
        }
        resolve({
          error: "UNKNOWN",
          message: "Unable to read your location. Pick a city manually or try Near me again.",
        });
      },
      { enableHighAccuracy, timeout, maximumAge }
    );
  });
}

export async function getCurrentPosition(options = {}) {
  const {
    enableHighAccuracy = false,
    timeout = 15000,
    maximumAge = 60000,
  } = options;

  lastGeolocationError = null;

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    setGeolocationError({
      error: "UNSUPPORTED",
      message: "This browser does not support location access. Pick a city manually.",
    });
    return null;
  }

  if (enableHighAccuracy) {
    // Caller needs a precise fix (e.g. caregiver distance search) — try real
    // GPS first. Coarse Wi-Fi/IP positioning can be many km off and collapse
    // customers in different sectors of the same city onto the same point.
    const high = await requestPosition({
      enableHighAccuracy: true,
      timeout: Math.max(timeout, 20000),
      maximumAge,
    });
    if (high?.latitude != null && high?.longitude != null) return high;
    const low = await requestPosition({ enableHighAccuracy: false, timeout, maximumAge });
    if (low?.latitude != null && low?.longitude != null) return low;
    setGeolocationError(low?.error ? low : high?.error ? high : low);
    return null;
  }

  // Low-accuracy first — works on more desktops; high-accuracy often times out without GPS hardware.
  const low = await requestPosition({ enableHighAccuracy: false, timeout, maximumAge });
  if (low?.latitude != null && low?.longitude != null) return low;

  setGeolocationError(low?.error ? low : null);
  return null;
}
